import { watch } from 'vue';
import { useAuthStore } from '@/stores/auth.store';
import { usePermissionStore } from '@/stores/permission.store';
import { selectedOrgId } from '@/common/utils/role.utils';
import type { User as PermissionsUser } from '@boqnet/permissions';

/**
 * Wires authStore → permissionStore so CASL abilities stay in sync with the
 * logged-in user and active organisation. This is the ONLY place that imports
 * both stores, deliberately breaking the circular dependency that would arise
 * if permissionStore reached into authStore directly.
 *
 * Should be called once during app bootstrap after Pinia is installed
 * (e.g. inside App.vue's onMounted).
 */
export const initPermissionsWatcher = (): void => {
  const authStore = useAuthStore();
  const permissionStore = usePermissionStore();

  watch(
    [() => authStore.loggedInUser, () => selectedOrgId.value],
    ([user]) => {
      // Cast to PermissionsUser only when the user has the required shape;
      // pass null (guest) otherwise so the ability is cleared on logout.
      const permissionsUser =
        user?.id && user.roles?.organisations ? (user as unknown as PermissionsUser) : null;

      permissionStore.setUser(permissionsUser);
    },
    { deep: true, immediate: true },
  );
};
